import React, { useState, useCallback, Fragment } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import Button from '../Common/Button';
import Toggle from '../Common/Toggle/Toggle';
import Notification from '../../components/Common/Notification';
import Modal from '../Common/Modal/Modal';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import {
	ActiveToggleFormProps,
	CheckedTask,
	InvalidTask,
	ValidationResult,
} from '../../apis/activeToggleForm/activeToggleForm.types';
import { useActiveToggle } from '../../apis/baselineTasks';

// Error Fallback Component
const ErrorFallback: React.FC<{ error: unknown; resetErrorBoundary: () => void }> = ({ 
	error, 
	resetErrorBoundary 
}) => (
	<div className="alert alert-danger m-3">
		<h5>Something went wrong with the active toggle form:</h5>
		<p>{error instanceof Error ? error.message : String(error)}</p>
		<button className="btn btn-secondary" onClick={resetErrorBoundary}>
			Try again
		</button>
	</div>
);

// Main Content Component
const ActiveToggleFormContent: React.FC<ActiveToggleFormProps> = ({
	tasksChecked,
	listingRefresh,
}) => {
	// Custom hook for active toggle mutation
	const { mutate: activeToggleMutate, isPending: isToggling } = useActiveToggle(
		(result) => {
			if (result.isError && result.errors && result.errors.length > 0) {
				// Hard error from the server — surface error messages as the invalid-task modal
				const invalidTasksData = result.errors.map((error: any, index: number) => ({
					id: index,
					name: typeof error === 'string' ? error : error.message || 'Invalid task'
				}));
				setIsLoadingState(false);
				setShowErrorModal(true);
				setInvalidTasks(invalidTasksData);
				setCopied(false);
			} else if (result.payload?.invalidBaselineTasks && result.payload.invalidBaselineTasks.length > 0) {
				// Partial success — some tasks couldn't be toggled due to prerequisite relationships
				setIsLoadingState(false);
				setShowErrorModal(true);
				setInvalidTasks(result.payload.invalidBaselineTasks);
				setCopied(false);
				listingRefresh();
			} else {
				const checkedTasksCount = tasksChecked?.length || 0;
				const successText = `Task${checkedTasksCount > 1 ? '(s)' : ''} successfully ${isActive ? 'activated' : 'inactivated'}`;
				Notification("success", "Success", successText);
				setIsLoadingState(false);
				listingRefresh();
			}
		},
	);
	
	// Component state - separate state variables
	const [isLoadingState, setIsLoadingState] = useState<boolean>(false);
	const [isActive, setIsActive] = useState<boolean>(true);
	const [isLocationTaskListActive, setIsLocationTaskListActive] = useState<boolean>(false);
	const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
	const [invalidTasks, setInvalidTasks] = useState<InvalidTask[]>([]);
	const [copied, setCopied] = useState<boolean>(false);

	// Update loading state when mutation is pending
	const isLoading = isToggling || isLoadingState;

	// Toggle change handler
	const onToggleChange = useCallback((property: string, value: boolean) => {
		if (property === 'isActive') {
			setIsActive(value);
		} else if (property === 'isLocationTaskListActive') {
			setIsLocationTaskListActive(value);
		}
	}, []);

	// Validation function
	const validate = useCallback((): ValidationResult => {
		if (!tasksChecked || tasksChecked.length < 1) {
			return { isValid: false, errors: ['No tasks selected'] };
		}
		
		// All selected tasks must be different from the target state (all active tasks for inactivation, or vice versa)
		const invalidTask = tasksChecked.find((t: CheckedTask) => t.isActive === isActive);
		if (invalidTask) {
			const activeString = isActive ? "active" : "inactive";
			const errorMessage = `Baseline Task "${invalidTask.name}" is already ${activeString}`;
			Notification("error", "Active Toggle Error", errorMessage);
			return { isValid: false, errors: [errorMessage] };
		}

		return { isValid: true };
	}, [tasksChecked, isActive]);

	// Save click handler
	const onSaveClick = useCallback(() => {
		const validationResult = validate();
		if (validationResult.isValid) {
			saveToggle();
		}
	}, [validate, isActive, isLocationTaskListActive]);

	// Save toggle function using TanStack Query mutation
	const saveToggle = useCallback(async () => {
		try {
			setIsLoadingState(true);
			const checkedTasks = tasksChecked.map((bt: CheckedTask) => bt.id);
			activeToggleMutate({
				baselineTaskIds: checkedTasks,
				isActive: isActive,
				isLocationTaskListActive: isLocationTaskListActive
			});
		} catch (error) {
			const errorMessage = error instanceof Error ? error.message : String(error);
			Notification("error", "Error", errorMessage);
			setIsLoadingState(false);
		}
	}, [tasksChecked, isActive, isLocationTaskListActive, activeToggleMutate]);

	// Close modal handler
	const closeModalWarning = useCallback(() => {
		setShowErrorModal(false);
		setInvalidTasks([]);
	}, []);

	// Copy state handler
	const onCopy = useCallback(() => {
		setCopied(true);
	}, []);

	// Render computed values
	const taskCount = tasksChecked?.length || 0;
	const taskText = taskCount === 1 ? "Task" : "Tasks";
	const selectedText = `${taskCount} ${taskText} Selected`;

	// Modal content
	const modalBody = (
		<div>
			<p>The relationship to the prerequisite needs to be removed before the task(s) can be made inactive:</p>
			{invalidTasks.slice(0, 10).map((t: InvalidTask) => (
				<p className="invalid-task-item" key={t.id}>{t.name}</p>
			))}
			<p>Total: {invalidTasks.length}</p>
		</div>
	);

	const modalFooter = (
		<div>
			<CopyToClipboard 
				options={{ format: "text/plain" }}
				text={invalidTasks.map((t: InvalidTask) => t.name).join(',')}
				onCopy={onCopy}
			>
				<button 
					type="button" 
					className={copied ? "btn btn-light" : "btn btn-primary"}
				>
					{copied ? "Copied!" : "Copy Error Details"}
				</button>
			</CopyToClipboard>
			<button 
				type="button" 
				className="btn btn-primary" 
				data-dismiss="modal" 
				onClick={closeModalWarning}
			>
				Ok
			</button>
		</div>
	);

	return (
		<Fragment>
			<div className="container">
				<div className="row w-100 toggle-dockbar">
					<div className="col-3">
						<span className="align-middle">{selectedText}</span>
					</div>
					<div className="col-2">
						<span className="align-middle">Mark Selected Tasks As:</span>
					</div>
					<div className="col-2">
						<Toggle 
							name="isActive" 
							text="Active" 
							checked={isActive} 
							onChange={onToggleChange} 
						/>
					</div>
					<div className="col-4">
						{!isActive && (
							<Toggle 
								name="isLocationTaskListActive" 
								text="Visible in Location Task List" 
								checked={isLocationTaskListActive} 
								onChange={onToggleChange} 
							/>
						)}
					</div>
					<div className="col-1">
						<div className="float-end">
							<Button 
								type="button" 
								bootstrapClass="primary" 
								text="Apply Changes" 
								onClick={onSaveClick}
								isDisabled={isLoading}
							/>
						</div>
					</div>
				</div>
			</div>

			{showErrorModal && (
				<Modal
					id="managerWarning"
					title="Error: Contingent Tasks"
					body={modalBody}
					onCloseClick={closeModalWarning}
					customFooter={modalFooter} 
				/>
			)}
		</Fragment>
	);
};

// Main Export Component with Error Boundary
const ActiveToggleForm: React.FC<ActiveToggleFormProps> = (props) => {
	return (
		<ErrorBoundary
			FallbackComponent={ErrorFallback}
			onReset={() => window.location.reload()}
		>
			<ActiveToggleFormContent {...props} />
		</ErrorBoundary>
	);
};

export default ActiveToggleForm;